import { dimensionDefinition, layoutBounds, lookOf, type DimensionLayout } from '../dimensions';
import { definitionOf } from '../entities';
import { compile, dependencies, evaluateCompiled, variableNameProblem, type Scope } from '../expr';
import { bounds, unionBounds, type Bounds, type Shape } from '../geom';
import {
  allNodes,
  isDimension,
  isEntity,
  isView,
  evaluateValue,
  isEffectivelyVisible,
  mergeStyle,
  type DimensionKind,
  type DrawingDocument,
  type EntityNode,
  type EntityType,
  type LayerId,
  type NodeId,
  type Style,
  type Value,
  type Variable,
  type ViewNode,
} from '../model';
import { viewLayout } from '../views';

export interface ResolvedVariables {
  readonly scope: Scope;
  readonly errors: Readonly<Record<string, string>>;
}

export interface ResolvedEntity {
  readonly id: NodeId;
  readonly type: EntityType;
  /** False when the entity, a group around it, or its layer is hidden. */
  readonly visible: boolean;
  /** False on a locked layer: it still shows and snaps, but cannot be picked. */
  readonly selectable: boolean;
  readonly layerId: LayerId;
  readonly shape: Shape;
  /** The layer's style with the entity's overrides on top; absent when neither sets one. */
  readonly style?: Style | undefined;
}

/** A dimension as drawn: its value and layout, with the same visibility rules as an entity. */
export interface ResolvedDimension {
  readonly id: NodeId;
  readonly kind: DimensionKind;
  readonly visible: boolean;
  readonly selectable: boolean;
  readonly layerId: LayerId;
  /** A length, an angle in degrees, or null for a callout, which measures nothing. */
  readonly value: number | null;
  readonly layout: DimensionLayout;
  readonly style?: Style | undefined;
}

/** A view's frame and title as drawn, with the same visibility rules as an entity. */
export interface ResolvedView {
  readonly id: NodeId;
  /** Its title, which renders and exports name it by. */
  readonly name: string;
  readonly visible: boolean;
  readonly selectable: boolean;
  readonly layerId: LayerId;
  readonly memberIds: readonly NodeId[];
  readonly layout: DimensionLayout;
  readonly style?: Style | undefined;
}

/**
 * What surfaces draw. Dimensions and views are kept apart from entities, so snapping,
 * measuring and script questions, which read only entities, never see them.
 */
export interface Drawn {
  readonly entities: readonly ResolvedEntity[];
  readonly dimensions: readonly ResolvedDimension[];
  readonly views: readonly ResolvedView[];
}

/** Anything drawn as a layout of strokes, fills and text rather than as a shape. */
export type ResolvedLayout = ResolvedDimension | ResolvedView;

/** Everything drawn as a layout: dimensions, then views. */
export const layoutsOf = (drawn: Drawn): readonly ResolvedLayout[] => [
  ...drawn.dimensions,
  ...drawn.views,
];

export interface ResolvedScene extends Drawn {
  readonly variables: ResolvedVariables;
  readonly errors: Readonly<Record<NodeId, string>>;
}

export function resolveVariables(variables: readonly Variable[]): ResolvedVariables {
  const scope: Record<string, number> = {};
  const errors: Record<string, string> = {};
  // Saved drawings can predate name validation, so bad names are flagged rather than refused.
  const byName = new Map<string, Variable>();
  for (const variable of variables) {
    const problem = variableNameProblem(variable.name, byName.keys());
    if (problem) errors[variable.id] = problem;
    else byName.set(variable.name, variable);
  }
  const state = new Map<string, 'visiting' | 'done'>();

  const visit = (variable: Variable): void => {
    if (state.get(variable.name) === 'done') return;
    if (state.get(variable.name) === 'visiting') {
      errors[variable.id] = `Circular reference through "${variable.name}"`;
      return;
    }
    state.set(variable.name, 'visiting');

    const compiled = compile(variable.expr);
    if (typeof compiled === 'string') {
      errors[variable.id] = compiled;
      state.set(variable.name, 'done');
      return;
    }

    for (const name of dependencies(compiled)) {
      const dependency = byName.get(name);
      if (dependency && dependency.id !== variable.id) visit(dependency);
    }

    state.set(variable.name, 'done');
    if (errors[variable.id]) return;

    const result = evaluateCompiled(compiled, scope);
    if (result.error !== null) errors[variable.id] = result.error;
    else if (result.value !== null) scope[variable.name] = result.value;
  };

  for (const variable of byName.values()) visit(variable);
  return { scope, errors };
}

export function resolveEntity(
  node: EntityNode,
  scope: Scope,
): { shape: Shape; error: null } | { shape: null; error: string } {
  const errors: string[] = [];

  const number = (value: Value): number => {
    const result = evaluateValue(value, scope);
    if (result.error !== null) errors.push(result.error);
    return result.value ?? 0;
  };

  const shape = definitionOf(node.type).resolve(node, number);

  if (errors.length > 0) return { shape: null, error: errors[0] as string };
  return { shape, error: null };
}

export function resolveDocument(doc: DrawingDocument): ResolvedScene {
  const variables = resolveVariables(doc.variables);
  const entities: ResolvedEntity[] = [];
  const dimensions: ResolvedDimension[] = [];
  const viewNodes: ViewNode[] = [];
  const errors: Record<NodeId, string> = {};
  const layers = new Map(doc.layers.map((layer) => [layer.id, layer]));

  for (const node of allNodes(doc)) {
    if (isEntity(node)) {
      const resolved = resolveEntity(node, variables.scope);
      if (resolved.error !== null) {
        errors[node.id] = resolved.error;
        continue;
      }
      const layer = layers.get(node.layerId);
      entities.push({
        id: node.id,
        type: node.type,
        visible: (layer?.visible ?? true) && isEffectivelyVisible(doc, node.id),
        selectable: !(layer?.locked ?? false),
        layerId: node.layerId,
        shape: resolved.shape,
        style: mergeStyle(layer?.style, node.style),
      });
    } else if (isDimension(node)) {
      const layer = layers.get(node.layerId);
      const style = mergeStyle(layer?.style, node.style);
      const measured = dimensionDefinition(node.kind).resolve(node, lookOf(style, doc.display));
      if (typeof measured === 'string') {
        errors[node.id] = measured;
        continue;
      }
      dimensions.push({
        id: node.id,
        kind: node.kind,
        visible: (layer?.visible ?? true) && isEffectivelyVisible(doc, node.id),
        selectable: !(layer?.locked ?? false),
        layerId: node.layerId,
        value: measured.value,
        layout: measured.layout,
        style,
      });
    } else if (isView(node)) {
      viewNodes.push(node);
    }
  }

  // Views go last, since a frame is sized by what was resolved inside it.
  const views: ResolvedView[] = [];
  const extents = new Map<NodeId, Bounds>();
  if (viewNodes.length > 0) {
    for (const each of entities) extents.set(each.id, bounds(each.shape));
    for (const each of dimensions) extents.set(each.id, layoutBounds(each.layout));
  }
  for (const node of viewNodes) {
    const inside = node.memberIds.flatMap((id) => extents.get(id) ?? []);
    if (inside.length === 0) {
      errors[node.id] = `View "${node.name}" has nothing drawn in it`;
      continue;
    }
    const layer = layers.get(node.layerId);
    const style = mergeStyle(layer?.style, node.style);
    views.push({
      id: node.id,
      name: node.name,
      visible: (layer?.visible ?? true) && isEffectivelyVisible(doc, node.id),
      selectable: !(layer?.locked ?? false),
      layerId: node.layerId,
      memberIds: node.memberIds,
      layout: viewLayout(
        inside.reduce(unionBounds),
        node.margin,
        node.name,
        lookOf(style, doc.display),
      ),
      style,
    });
  }

  const order = new Map(doc.layers.map((layer, index) => [layer.id, index]));
  const count = doc.layers.length;
  return {
    variables,
    entities: inLayerOrder(entities, order, count),
    dimensions: inLayerOrder(dimensions, order, count),
    views: inLayerOrder(views, order, count),
    errors,
  };
}

/** Bottom layer first, keeping script order within a layer, so later paints and picks on top. */
function inLayerOrder<T extends { readonly layerId: LayerId }>(
  items: T[],
  order: ReadonlyMap<LayerId, number>,
  count: number,
): T[] {
  if (count < 2) return items;
  const buckets: T[][] = Array.from({ length: count }, () => []);
  for (const item of items) buckets[order.get(item.layerId) ?? 0]!.push(item);
  return buckets.flat();
}
